// ===========================================
// REALTIME STATS HOOK FOR MANAGER DASHBOARD
// ===========================================

import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useWebSocket, WebSocketMessage } from '@mise/shared';

// Only use WebSocket if explicitly configured - otherwise rely on polling
const WS_URL = import.meta.env.VITE_WS_URL || null;
const WS_ENABLED = !!import.meta.env.VITE_WS_URL;

export interface RealtimeAlert {
  id: string;
  type: 'info' | 'warning' | 'error' | 'success';
  title: string;
  message: string;
  timestamp: Date;
  dismissed: boolean;
}

export function useRealtimeStats() {
  const queryClient = useQueryClient();
  const [alerts, setAlerts] = useState<RealtimeAlert[]>([]);

  // Get auth token from storage
  const getToken = useCallback(() => {
    // Try direct token first
    const directToken = localStorage.getItem('manager_token');
    if (directToken) return directToken;

    // Fall back to zustand persisted state
    try {
      const auth = localStorage.getItem('manager-auth');
      if (auth) {
        const parsed = JSON.parse(auth);
        return parsed.state?.token || null;
      }
    } catch {
      return null;
    }
    return null;
  }, []);

  // Add an alert
  const addAlert = useCallback((alert: Omit<RealtimeAlert, 'id' | 'timestamp' | 'dismissed'>) => {
    const newAlert: RealtimeAlert = {
      ...alert,
      id: `alert-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      dismissed: false,
    };
    setAlerts((prev) => [newAlert, ...prev].slice(0, 50)); // Keep last 50 alerts
  }, []);

  // Dismiss an alert
  const dismissAlert = useCallback((alertId: string) => {
    setAlerts((prev) =>
      prev.map((a) => (a.id === alertId ? { ...a, dismissed: true } : a))
    );
  }, []);

  // Clear all alerts
  const clearAlerts = useCallback(() => {
    setAlerts([]);
  }, []);

  // Handle WebSocket messages
  const handleMessage = useCallback((message: WebSocketMessage) => {
    switch (message.type) {
      // Sales/Analytics events
      case 'payment.processed':
      case 'payment.completed':
        queryClient.invalidateQueries({ queryKey: ['analytics'] });
        queryClient.invalidateQueries({ queryKey: ['sales'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
        queryClient.invalidateQueries({ queryKey: ['tables'] });
        queryClient.invalidateQueries({ queryKey: ['table-stats'] });
        break;

      // Order events
      case 'order.created':
      case 'order.completed':
        queryClient.invalidateQueries({ queryKey: ['orders'] });
        queryClient.invalidateQueries({ queryKey: ['analytics'] });
        break;

      // Staff events
      case 'staff.clocked_in':
        queryClient.invalidateQueries({ queryKey: ['staff'] });
        queryClient.invalidateQueries({ queryKey: ['timesheets'] });
        addAlert({
          type: 'info',
          title: 'Staff Clocked In',
          message: `${message.payload?.employeeName || 'An employee'} has clocked in`,
        });
        break;

      case 'staff.clocked_out':
        queryClient.invalidateQueries({ queryKey: ['staff'] });
        queryClient.invalidateQueries({ queryKey: ['timesheets'] });
        addAlert({
          type: 'info',
          title: 'Staff Clocked Out',
          message: `${message.payload?.employeeName || 'An employee'} has clocked out`,
        });
        break;

      // Table events - comprehensive invalidation for cross-view sync
      case 'table.seated':
        queryClient.invalidateQueries({ queryKey: ['tables'] });
        queryClient.invalidateQueries({ queryKey: ['sections'] }); // POS
        queryClient.invalidateQueries({ queryKey: ['table-stats'] });
        queryClient.invalidateQueries({ queryKey: ['floor-plan'] });
        queryClient.invalidateQueries({ queryKey: ['host-overview'] });
        queryClient.invalidateQueries({ queryKey: ['reservations'] });
        queryClient.invalidateQueries({ queryKey: ['guests'] });
        addAlert({
          type: 'info',
          title: 'Table Seated',
          message: `Table ${message.payload?.tableNumber || '?'} - Party of ${message.payload?.partySize || '?'}`,
        });
        break;

      case 'table.status_changed':
      case 'table.cleared':
        queryClient.invalidateQueries({ queryKey: ['tables'] });
        queryClient.invalidateQueries({ queryKey: ['sections'] }); // POS
        queryClient.invalidateQueries({ queryKey: ['table-stats'] });
        queryClient.invalidateQueries({ queryKey: ['floor-plan'] });
        queryClient.invalidateQueries({ queryKey: ['host-overview'] });
        queryClient.invalidateQueries({ queryKey: ['reservations'] });
        break;

      case 'table.moved':
        queryClient.invalidateQueries({ queryKey: ['tables'] });
        queryClient.invalidateQueries({ queryKey: ['sections'] }); // POS
        queryClient.invalidateQueries({ queryKey: ['table-stats'] });
        queryClient.invalidateQueries({ queryKey: ['floor-plan'] });
        queryClient.invalidateQueries({ queryKey: ['host-overview'] });
        queryClient.invalidateQueries({ queryKey: ['reservations'] });
        addAlert({
          type: 'info',
          title: 'Table Moved',
          message: `Party moved from Table ${message.payload?.fromTableNumber || '?'} to Table ${message.payload?.toTableNumber || '?'}`,
        });
        break;

      // Reservation events
      case 'reservation.checked_in':
        queryClient.invalidateQueries({ queryKey: ['reservations'] });
        queryClient.invalidateQueries({ queryKey: ['host-overview'] });
        queryClient.invalidateQueries({ queryKey: ['tables'] });
        queryClient.invalidateQueries({ queryKey: ['sections'] }); // POS
        addAlert({
          type: 'info',
          title: 'Guest Checked In',
          message: message.payload?.guestName ? `${message.payload.guestName} has arrived` : 'A guest has checked in',
        });
        break;

      case 'reservation.created':
      case 'reservation.updated':
      case 'reservation.cancelled':
        queryClient.invalidateQueries({ queryKey: ['reservations'] });
        queryClient.invalidateQueries({ queryKey: ['host-overview'] });
        queryClient.invalidateQueries({ queryKey: ['tables'] });
        queryClient.invalidateQueries({ queryKey: ['sections'] }); // POS
        queryClient.invalidateQueries({ queryKey: ['guests'] });
        break;

      case 'check.closed':
        queryClient.invalidateQueries({ queryKey: ['tables'] });
        queryClient.invalidateQueries({ queryKey: ['sections'] }); // POS
        queryClient.invalidateQueries({ queryKey: ['table-stats'] });
        queryClient.invalidateQueries({ queryKey: ['floor-plan'] });
        queryClient.invalidateQueries({ queryKey: ['host-overview'] });
        queryClient.invalidateQueries({ queryKey: ['reservations'] });
        queryClient.invalidateQueries({ queryKey: ['analytics'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
        addAlert({
          type: 'success',
          title: 'Table Closed',
          message: `Table ${message.payload?.tableNumber || '?'} is now available`,
        });
        break;

      // Inventory alerts
      case 'inventory.low':
        queryClient.invalidateQueries({ queryKey: ['inventory'] });
        addAlert({
          type: 'warning',
          title: 'Low Inventory',
          message: `${message.payload?.itemName || 'An item'} is running low`,
        });
        break;

      case 'inventory.depleted':
        queryClient.invalidateQueries({ queryKey: ['inventory'] });
        addAlert({
          type: 'error',
          title: 'Item Out of Stock',
          message: `${message.payload?.itemName || 'An item'} is now out of stock`,
        });
        break;

      // Kitchen events
      case 'kitchen.item_86d':
        queryClient.invalidateQueries({ queryKey: ['menu'] });
        addAlert({
          type: 'warning',
          title: 'Item 86\'d',
          message: `${message.payload?.itemName || 'An item'} has been 86'd`,
        });
        break;

      case 'kitchen.item_available':
        queryClient.invalidateQueries({ queryKey: ['menu'] });
        break;

      // AI events
      case 'ai.alert_triggered':
        addAlert({
          type: message.payload?.severity === 'high' ? 'error' : 'warning',
          title: message.payload?.title || 'AI Alert',
          message: message.payload?.message || 'AI system generated an alert',
        });
        break;

      case 'ai.prediction_generated':
        queryClient.invalidateQueries({ queryKey: ['predictions'] });
        break;

      default:
        // Unknown event type, ignore
        break;
    }
  }, [queryClient, addAlert]);

  // WebSocket connection - only if explicitly configured via VITE_WS_URL
  const {
    isConnected: wsConnected,
    isConnecting,
    error,
    subscribe,
    reconnect,
  } = useWebSocket({
    url: WS_URL || '', // Empty string will prevent connection attempt
    token: WS_ENABLED ? getToken() : null, // Pass null token if WS not configured (prevents connection)
    onMessage: handleMessage,
    autoReconnect: WS_ENABLED, // Only auto-reconnect if WebSocket is configured
    onConnected: () => {
      console.log('[Dashboard] WebSocket connected');
      // Subscribe to all relevant channels
      subscribe('orders');
      subscribe('tables');
      subscribe('staff');
      subscribe('inventory');
      subscribe('kitchen');
      subscribe('analytics');
    },
    onDisconnected: () => {
      if (WS_ENABLED) {
        console.log('[Dashboard] WebSocket disconnected');
      }
    },
  });

  // If WebSocket is not configured, consider it "connected" (using polling instead)
  // This prevents the "Reconnecting..." indicator from showing
  const isConnected = WS_ENABLED ? wsConnected : true;

  // Get active (non-dismissed) alerts
  const activeAlerts = alerts.filter((a) => !a.dismissed);

  return {
    isConnected,
    isConnecting,
    error,
    reconnect,
    alerts: activeAlerts,
    allAlerts: alerts,
    dismissAlert,
    clearAlerts,
    addAlert,
  };
}
